import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { registerApi, googleLoginApi } from '@/utils/api'
import { GoogleLogin } from '@react-oauth/google'
import { useTranslation } from '@/i18n'
import i18n from '@/i18n/config'

const SignupPage: React.FC = () => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Force English language for this page
  useEffect(() => {
    i18n.changeLanguage('en-US')
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    // Validate form
    if (password !== confirmPassword) {
      setError(t('signup.error.passwordMismatch'))
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError(t('signup.error.passwordTooShort'))
      setLoading(false)
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError(t('signup.error.invalidEmail'))
      setLoading(false)
      return
    }

    try {
      // API call to register user
      const response = await registerApi({
        email,
        password,
        nickname: name || email.split('@')[0] // Use name or email prefix as nickname
      })

      // Handle success response
      if (response.code === 200) {
        console.log('Registration successful:', response.data)
        // Optionally store user info or token in localStorage
        // localStorage.setItem('user', JSON.stringify(response.data))

        setLoading(false)
        // Redirect to login page with updated message, clearing any previous state
        navigate('/login', {
          replace: true, // Replace the current entry to prevent back button issues
          state: {
            message: t('signup.successMessage')
          }
        })
      } else {
        setError(response.message || 'Registration failed')
        setLoading(false)
      }
    } catch (err: any) {
      console.error('Registration error:', err)
      // Handle both string and object error responses
      const errorMessage = typeof err === 'string' ? err : err.message || 'An unexpected error occurred'
      setError(errorMessage)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="text-center">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-indigo-600 bg-clip-text text-transparent mb-2">
            VisSynth
          </h1>
          <p className="text-gray-400">{t('signup.createAccount')}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Signup Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                {t('signup.fullName')}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                {t('signup.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                {t('signup.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-300 mb-1">
                {t('signup.confirmPassword')}
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-800 bg-gray-900 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-950 transition-all shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  {t('signup.creatingAccount')}
                </div>
              ) : (
                t('signup.signup')
              )}
            </button>
          </div>

          {/* Terms */}
          <div className="text-xs text-center text-gray-500">
            {t('signup.byContinuing')}{' '}
            <Link to="#" className="text-blue-400 hover:text-blue-300 transition-colors">
              {t('signup.termsOfService')}
            </Link>{' '}
            {t('signup.and')}{' '}
            <Link to="#" className="text-blue-400 hover:text-blue-300 transition-colors">
              {t('signup.privacyPolicy')}
            </Link>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-950 text-gray-500">{t('signup.orContinueWith')}</span>
            </div>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-1 gap-3">
            <GoogleLogin
              locale="en-US"
              onSuccess={async (credentialResponse) => {
                try {
                  setLoading(true)
                  const response = await googleLoginApi({
                    id_token: credentialResponse.credential!
                  })

                  if (response.code === 200) {
                    // Store user info and token in localStorage
                    localStorage.setItem('token', response.data.token)
                    localStorage.setItem('user', JSON.stringify({
                      user_id: response.data.user_id,
                      nickname: response.data.nickname
                    }))

                    // Redirect to home page
                    navigate('/', { replace: true })
                  } else {
                    setError(response.message || 'Google signup failed')
                    setLoading(false)
                  }
                } catch (err: any) {
                  console.error('Google signup error:', err)
                  const errorMessage = typeof err === 'string' ? err : err.message || 'An unexpected error occurred'
                  setError(errorMessage)
                  setLoading(false)
                }
              }}
              onError={() => {
                setError(t('signup.error.googleSignupFailed'))
                setLoading(false)
              }}
            >
              {(renderProps) => (
                <button
                  onClick={renderProps.onClick}
                  disabled={renderProps.disabled}
                  className="w-full py-3 px-4 border border-gray-800 bg-gray-900 text-white rounded-lg flex items-center justify-center gap-2 font-medium hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133.48 12.48.48c-8.84 0-16 7.16-16 16s7.16 16 16 16c9.235 0 15.36-6.49 15.36-15.6 0-.883-.107-1.746-.354-2.592l-1.968 1.968z" />
                  </svg>
                  Sign up with Google
                </button>
              )}
            </GoogleLogin>
          </div>
        </form>

        {/* Login Link */}
        <div className="text-center">
          <p className="text-sm text-gray-400">
            {t('signup.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
              {t('signup.login')}
            </Link>
          </p>
        </div>
      </div>

    </div>
  )
}

export default SignupPage
